// Personalization fields of the product page (<product-personalization>,
// blocks/product-personalization.liquid). The fields themselves are plain
// form controls tied to the product form; the browser already refuses to
// submit while a required one is empty. This adds what the browser does not:
// the message in Portuguese next to the field, the photo previewed in the
// drop area, the character count, the Foto | Texto switch of a slot that
// takes either (only the chosen side stays enabled, so the browser asks for
// that one and the form carries that one), and one case of the add to cart.
//
// That case: a photo plus a ticked "Leve junto" or kit add-on. cart.js sends
// add-ons as one JSON request, and JSON cannot carry a file. So the add-ons
// go first, on their own, and then the form submits again with them
// unticked, down cart.js's plain FormData path, which uploads the photo and
// opens the drawer with everything in it. The ticks come back right after:
// cart.js reads the form before its first await.

class ProductPersonalization extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.form);
    this.maxBytes = Number(this.dataset.maxMb || 15) * 1024 * 1024;
    this.urls = [];
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.addEventListener('change', (event) => {
      const mode = event.target.closest('[data-mode]');
      if (mode) return this.mode(mode);
      const input = event.target.closest('[data-photo-input]');
      if (input) this.photo(input);
    }, { signal });

    this.addEventListener('input', (event) => {
      const input = event.target.closest('[data-text-input]');
      if (!input) return;
      this.count(input);
      this.clear(input);
    }, { signal });

    // invalid does not bubble: listened in the capture phase.
    this.addEventListener('invalid', (event) => this.flag(event.target), { capture: true, signal });

    // On the form itself, so it runs before cart.js, which listens on the
    // document.
    this.form?.addEventListener('submit', (event) => this.submit(event), { signal });

    this.querySelectorAll('[data-text-input]').forEach((input) => this.count(input));
    // Hidden in the markup: without this script the photo is what is asked.
    this.querySelectorAll('[data-mode-switch]').forEach((group) => { group.hidden = false; });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.urls.forEach((url) => URL.revokeObjectURL(url));
  }

  box(input) {
    return input.closest('[data-photo], .product-personalization__field');
  }

  say(input, message) {
    const box = this.box(input);
    box?.toggleAttribute('data-invalid', Boolean(message));
    const error = box?.querySelector('[data-error]');
    if (error) error.textContent = message;
  }

  clear(input) {
    input.setCustomValidity('');
    this.say(input, '');
  }

  // The browser points at the field; this says why, in the store's words.
  flag(input) {
    if (!input.validity.valueMissing) return;
    const message = input.matches('[data-photo-input]') ? this.dataset.photoMissing : this.dataset.textMissing;
    input.setCustomValidity(message || '');
    this.say(input, message || '');
  }

  photo(input) {
    const box = this.box(input);
    const file = input.files?.[0];
    const preview = box.querySelector('[data-photo-preview]');
    const name = box.querySelector('[data-photo-file]');
    this.clear(input);

    let problem = '';
    if (file && !file.type.startsWith('image/') && !/\.(heic|heif)$/i.test(file.name)) problem = this.dataset.photoNotImage;
    else if (file && file.size > this.maxBytes) problem = this.dataset.photoTooBig;
    if (problem) {
      input.value = '';
      this.say(input, problem);
    }

    const chosen = Boolean(file) && !problem;
    box.toggleAttribute('data-filled', chosen);
    box.querySelector('[data-photo-note]').hidden = chosen;
    box.querySelector('[data-photo-choose]').hidden = chosen;
    box.querySelector('[data-photo-change]').hidden = !chosen;
    name.hidden = !chosen;
    name.textContent = chosen ? file.name : '';

    // A photo the browser cannot draw (HEIC on most desktops) keeps the
    // icon and shows its name only.
    preview.hidden = true;
    if (!chosen) return;
    const url = URL.createObjectURL(file);
    this.urls.push(url);
    preview.onload = () => { preview.hidden = false; };
    preview.onerror = () => { preview.hidden = true; };
    preview.src = url;
  }

  // One side of a Foto | Texto slot on, the other hidden and disabled: a
  // disabled field is neither required nor sent.
  mode(radio) {
    radio.closest('[data-slot]')?.querySelectorAll('[data-mode-panel]').forEach((panel) => {
      const on = panel.dataset.modePanel === radio.value;
      panel.hidden = !on;
      panel.querySelectorAll('[data-photo-input], [data-text-input]').forEach((input) => {
        input.disabled = !on;
        if (!on) this.clear(input);
      });
    });
  }

  count(input) {
    const counter = this.box(input)?.querySelector('[data-count]');
    if (counter && input.maxLength > 0) counter.textContent = `${input.value.length}/${input.maxLength}`;
  }

  submit(event) {
    if (this.passing) return;
    const photos = Array.from(this.querySelectorAll('[data-photo-input]')).some((input) => !input.disabled && input.files?.length);
    if (!photos) return;
    const addons = Array.from(this.form.elements).filter(
      (field) => field.matches('[data-addon-variant]') && field.checked && !field.disabled,
    );
    if (!addons.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.addAddons(addons);
  }

  async addAddons(addons) {
    const button = this.form.querySelector('[type="submit"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    button?.setAttribute('aria-busy', 'true');
    const status = this.form.querySelector('[data-add-status]');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: addons.map((field) => ({ id: Number(field.dataset.addonVariant), quantity: 1 })) }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || '');
      }
    } catch (error) {
      if (status) status.textContent = error.message || this.form.closest('[data-add-error]')?.dataset.addError || '';
      button?.removeAttribute('aria-busy');
      return;
    }

    button?.removeAttribute('aria-busy');
    addons.forEach((field) => { field.checked = false; });
    this.passing = true;
    this.form.requestSubmit();
    this.passing = false;
    addons.forEach((field) => { field.checked = true; });
  }
}

if (!customElements.get('product-personalization')) {
  customElements.define('product-personalization', ProductPersonalization);
}
